import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getIsTrainIncident } from '@rsos/base-utils';
import { ACCEPTED, DISPATCH_REQUESTED } from '@rsos/constants/alertStatuses';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import useSelectedTheme from '@rsos/hooks/useSelectedTheme';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import { MARKER_TYPE_GRAPHIC } from '@rsos/maps/constants/markerTypes';
import { createCircle } from '@rsos/maps/interface/circles';
import { createMarker } from '@rsos/maps/interface/markers';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import usePrevious from '@rsos/utils/usePrevious';
import PopupPortal from '../../PopupPortal';
import '../OverridePOPUP.css';
import AlertPopup from './AlertPopup';
import {
  getAlertMarkerIcon,
  getAlertMarkerSize,
  getAlertCircleColor,
} from './alertMarkerIcons';

const baseEsriCircle = {
  type: 'simple-fill',
  style: 'solid',
  color: getAlertCircleColor(),
  outline: { width: 2, color: getAlertCircleColor() },
};

export const TrainMarkers = ({
  activeQueueTab,
  map,
  onMarkerAdd,
  onRemoveID,
  removedID,
  handleMarkerHover,
  alerts,
  popupData,
  portalMount,
  trainMarkers,
  setTrainMarkers,
  trainMarkersLayer,
  trainMarkerCircles,
  setTrainMarkerCircles,
  trainMarkerCirclesLayer,
}) => {
  const IDWithNewAlert = useSelector(state => state.incidents.IDWithNewAlert);
  const declinedAlertID = useSelector(state => state.incidents.declinedAlertID);
  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const userProfile = useSelector(state => state.sinatra.user.profile);
  const isAllView = !selectedCallerID && !selectedAlertID;

  const themeName = useSelectedTheme();

  // Handle incoming alerts
  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI:
        if (IDWithNewAlert) {
          const alert = alerts[IDWithNewAlert];
          if (alert && getIsTrainIncident(alert.emergency_type.name, true)) {
            const isMarkerOpaque =
              activeQueueTab === EMERGENCY_CALLS &&
              alert.status.name === ACCEPTED;
            if (isAllView) {
              const latlng = {
                lat: alert?.location?.geodetic?.latitude,
                lng: alert?.location?.geodetic?.longitude,
              };
              const markerIcon = getAlertMarkerIcon(
                isAllView,
                false,
                alert,
                isMarkerOpaque,
              );
              const currentMarker = trainMarkers.find(marker => {
                return marker.custom.alertID === IDWithNewAlert;
              });

              if (currentMarker) {
                const options = {
                  position: latlng,
                };
                currentMarker.updatePosition(options);
                onMarkerAdd();
              } else {
                createMarker({
                  mapObject: map,
                  options: {
                    point: latlng,
                    icon: markerIcon,
                    iconSize: getAlertMarkerSize(isAllView),
                    custom: {
                      alertID: IDWithNewAlert,
                    },
                    layer: trainMarkersLayer,
                  },
                  markerType: MARKER_TYPE_GRAPHIC,
                }).then(resolvedMarker => {
                  trainMarkersLayer.add(resolvedMarker.instance);
                  setTrainMarkers([resolvedMarker, ...trainMarkers]);
                  onMarkerAdd();
                });
              }
            } else if (IDWithNewAlert === selectedAlertID) {
              const { location } = alerts[selectedAlertID];
              const currentMarker = trainMarkers.find(
                marker => marker.custom.alertID === selectedAlertID,
              );

              const latlng = {
                lat: parseFloat(location.geodetic?.latitude),
                lng: parseFloat(location.geodetic?.longitude),
              };

              if (currentMarker) {
                // Create new marker
                const markerIcon = getAlertMarkerIcon(
                  isAllView,
                  true,
                  alerts[currentMarker?.custom?.alertID],
                  isMarkerOpaque,
                );
                const markerSize = getAlertMarkerSize(
                  isAllView,
                  true,
                  alerts[currentMarker?.custom?.alertID],
                );
                createMarker({
                  mapObject: map,
                  options: {
                    point: latlng,
                    icon: markerIcon,
                    iconSize: markerSize,
                    custom: {
                      alertID: selectedAlertID,
                    },
                    layer: trainMarkersLayer,
                  },
                  markerType: MARKER_TYPE_GRAPHIC,
                }).then(resolvedMarker => {
                  // Update current marker
                  const newIcon = getAlertMarkerIcon(
                    isAllView,
                    false,
                    alerts[currentMarker?.custom?.alertID],
                    isMarkerOpaque,
                  );
                  const markerSize = getAlertMarkerSize(
                    isAllView,
                    false,
                    alerts[currentMarker?.custom?.alertID],
                  );
                  currentMarker.changeMarkerIcon({
                    icon: newIcon,
                    iconSize: markerSize,
                  });
                  panMapTo({
                    mapProvider: ESRI,
                    mapInstance: map.mapInstance,
                    latitude: latlng.lat,
                    longitude: latlng.lng,
                  });

                  onMarkerAdd();

                  trainMarkersLayer.add(resolvedMarker.instance);
                  setTrainMarkers([resolvedMarker, ...trainMarkers]);
                });

                const currentCircle = trainMarkerCircles.find(
                  circle => circle.custom.alertID === selectedAlertID,
                );
                if (currentCircle) {
                  // Old circle - change it to transparent
                  currentCircle.changeCircleColor({
                    symbolOptions: {
                      ...baseEsriCircle,
                      color: getAlertCircleColor(true),
                      outline: { width: 2, color: getAlertCircleColor(true) },
                    },
                  });
                  // New circle
                  const circleOptions = {
                    center: [latlng.lng, latlng.lat],
                    radius:
                      location.geodetic.uncertainty_radius < 0
                        ? 0
                        : parseFloat(location.geodetic.uncertainty_radius),
                  };
                  createCircle({
                    mapObject: map,
                    circleOptions: {
                      ...circleOptions,
                      custom: {
                        alertID: selectedAlertID,
                      },
                      layer: trainMarkerCirclesLayer,
                    },
                    graphicOptions: {
                      symbol: baseEsriCircle,
                      visible: true,
                    },
                  }).then(resolvedMarkerCircle => {
                    trainMarkerCirclesLayer.add(resolvedMarkerCircle.instance);
                    setTrainMarkerCircles([
                      resolvedMarkerCircle,
                      ...trainMarkerCircles,
                    ]);
                  });
                }
              }
            }
          }
        }
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IDWithNewAlert]);

  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI:
        // Find and remove corresponding marker
        if (removedID && trainMarkersLayer && trainMarkers.length) {
          const foundMarker = trainMarkers.find(marker => {
            return marker.custom.alertID === removedID;
          });
          if (foundMarker) {
            new Promise(resolve => {
              foundMarker.remove();
              resolve();
            }).then(() => {
              setTrainMarkers(
                trainMarkers.filter(
                  marker => marker.custom.alertID !== removedID,
                ),
              );
              onRemoveID();
            });
          }
        }
        break;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removedID]);

  useEffect(() => {
    if (declinedAlertID && trainMarkers.length) {
      setTrainMarkers(
        trainMarkers.filter(marker => {
          if (marker.custom.alertID === declinedAlertID) {
            marker.remove();
          }
          return marker.custom.alertID !== declinedAlertID;
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declinedAlertID]);

  // conrols the opacity of the marker on tab change
  useEffect(() => {
    trainMarkers.map((marker, idx) => {
      const alert = alerts[marker.custom.alertID];
      const isMarkerOpaque =
        trainMarkersLayer && activeQueueTab === EMERGENCY_CALLS;
      const newIcon = getAlertMarkerIcon(
        isAllView,
        idx === 0,
        alert,
        isMarkerOpaque && alert?.status.name === ACCEPTED,
      );
      const markerSize = getAlertMarkerSize(isAllView, idx === 0);
      marker.changeMarkerIcon({
        icon: newIcon,
        iconSize: markerSize,
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQueueTab, trainMarkersLayer]);

  // ensure to live update the train marker from yellow to purple for current and other users within the agency when accepted by someone else
  const prevAlerts = usePrevious(alerts);
  useEffect(() => {
    if (
      prevAlerts &&
      Object.keys(alerts).length === Object.keys(prevAlerts).length
    ) {
      Object.keys(alerts).forEach(a => {
        if (
          alerts[a]?.status.name === ACCEPTED &&
          prevAlerts[a]?.status.name === DISPATCH_REQUESTED
        ) {
          const currentMarker = trainMarkers.find(
            marker => marker.custom.alertID === alerts[a]?.alert_id,
          );

          if (currentMarker) {
            const isOpaque = activeQueueTab === EMERGENCY_CALLS;
            const newIcon = getAlertMarkerIcon(
              isAllView,
              true,
              alerts[a],
              isOpaque,
            );
            const markerSize = getAlertMarkerSize(isAllView, true);
            currentMarker.changeMarkerIcon({
              icon: newIcon,
              iconSize: markerSize,
            });
            handleMarkerHover();
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts]);
  // Conditional rendering based on map provider
  switch (map.mapProvider) {
    case ESRI:
      if (
        popupData &&
        getIsTrainIncident(popupData.alert?.emergency_type?.name, true)
      ) {
        const createdAt =
          popupData.alert.location_history[popupData.index].created_at;

        return (
          <PopupPortal mount={portalMount}>
            <AlertPopup
              userProfile={userProfile}
              isAllView={isAllView}
              data={popupData.alert}
              createdAt={createdAt}
              themeName={themeName}
            />
          </PopupPortal>
        );
      }
      return null;
    default:
      return null;
  }
};
